import "./style.scss";
import { IonAlert, IonButton, IonCol, IonGrid, IonRow } from "@ionic/react";
import { useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useUpdateWorkerCache } from "@src/appV2/Worker/useUpdateWorkerCache";
import { FC, useCallback, useState } from "react";

import { payout, updatePaymentSchedule } from "./api";
import { LegacyClipboardPaySchedule, PayrollSettingsDropdownProps } from "./models";
import { MessageString } from "../../constants/strings";
import { USER_EVENTS } from "../../constants/userEvents";
import { formatDollarsAsUsd } from "../../utils/currency";

const AutomaticPaySchedules: readonly LegacyClipboardPaySchedule[] = [
  "instantly",
  "daily",
] as const;

const IntervalText: Record<LegacyClipboardPaySchedule, string> = {
  instantly: "Instantly withdraw",
  daily: "At the end of the day",
};

const PayrollSettingsDropdown: FC<PayrollSettingsDropdownProps> = ({
  balance,
  currentPaySchedule,
  onPayScheduleChange,
}) => {
  const worker = useDefinedWorker();
  const [loading, setLoading] = useState(false);
  const { showErrorToast } = useToast();
  const updateWorkerCache = useUpdateWorkerCache();

  const [modalSettings, setModalSettings] = useState<
    Partial<{
      isOpen: boolean;
      message: string;
      title: string;
      confirmTextButton: string;
      confirmFunction: () => void;
      cancelFunction: () => void;
    }>
  >();
  const dismissModal = useCallback(() => {
    setModalSettings((settings) => ({ ...settings, isOpen: false }));
  }, []);

  const submitPaymentInterval = async (interval: LegacyClipboardPaySchedule) => {
    try {
      setLoading(true);
      const paymentAccountInfoUpdated = await updatePaymentSchedule({
        interval,
      });
      updateWorkerCache({
        paymentAccountInfo: paymentAccountInfoUpdated,
      });
      onPayScheduleChange(interval);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showErrorToast("Failed to save the new pay settings.");
      console.error(error);
    }
  };

  const paymentMethod = balance?.hasDebitCard ? "card" : "bank account";

  const handleSelectedOption = (option: LegacyClipboardPaySchedule) => {
    if (option === currentPaySchedule) {
      return;
    }
    if (option === "instantly") {
      logEvent(USER_EVENTS.AUTO_WITHDRAW_REQUESTED);
      setModalSettings({
        title: "Turn on Instant Payouts?",
        message: `After each transfer, we will immediately sent to your ${paymentMethod}
          and we will remove ${formatDollarsAsUsd(
            worker.paymentAccountInfo?.withdrawalFee as number
          )} to cover processing costs.`,
        isOpen: true,
        cancelFunction: () => {
          logEvent(USER_EVENTS.AUTO_WITHDRAW_REJECTED);
          dismissModal();
        },
        confirmFunction: async () => {
          dismissModal();
          await submitPaymentInterval("instantly");
          logEvent(USER_EVENTS.AUTO_WITHDRAW_CONFIRMED);
          try {
            await payout();
          } catch (error) {
            const defaultMessage = MessageString.payrollSummary.payoutFailedForUnknownReason;
            const message =
              error.status === 400
                ? (error.response?.body?.message ?? defaultMessage)
                : defaultMessage;
            showErrorToast(message);
          }
        },
        confirmTextButton: "Turn it On",
      });
    } else if (option === "daily") {
      setModalSettings({
        title: "Turn on Daily Payouts?",
        message: `If there’s balance in your account at the end of the day,
          Stripe will send funds for free to your ${paymentMethod} -- it will take 2-3 business days.`,
        isOpen: true,
        cancelFunction: () => {
          dismissModal();
        },
        confirmFunction: async () => {
          dismissModal();
          await submitPaymentInterval("daily");
          logEvent(USER_EVENTS.AUTO_WITHDRAW_DISABLED);
        },
        confirmTextButton: "Turn it On",
      });
    }
  };

  return (
    <>
      <IonGrid>
        <IonRow style={{ padding: 0, margin: "10px 0px" }}>
          {AutomaticPaySchedules.map((schedule) => {
            if (schedule === "instantly" && !balance.hasDebitCard) {
              return null;
            }
            const isSelected = schedule === currentPaySchedule;

            return (
              <IonCol
                key={schedule}
                size={(12 / AutomaticPaySchedules.length).toString()}
                className={`payroll-settings-automatic-option ${
                  isSelected ? "selected-option" : ""
                }`}
              >
                <IonButton
                  fill="default"
                  expand="block"
                  className="ion-text-wrap"
                  disabled={loading}
                  onClick={() => handleSelectedOption(schedule)}
                  aria-selected={isSelected}
                  /**
                   * `aria-selected` is the accessible attribute to indicate that something is selected.
                   * `aria-label` is not really valid here. The label should indicate what would happen
                   * if a user clicked this button.
                   */
                  aria-label={`${IntervalText[schedule]}${isSelected ? " selected" : ""}`}
                >
                  {IntervalText[schedule]}
                </IonButton>
              </IonCol>
            );
          })}
        </IonRow>
      </IonGrid>
      <IonAlert
        isOpen={modalSettings?.isOpen as boolean}
        onDidDismiss={dismissModal}
        header={modalSettings?.title}
        message={modalSettings?.message}
        backdropDismiss={true}
        /**
         * This animation sometimes makes the UI unresponsive, blocking
         * button clicks on the underlying page.
         * Disabling animation removes the issue. (observed in e2e tests)
         */
        animated={false}
        buttons={[
          {
            text: "Never Mind",
            role: "cancel",
            handler: modalSettings?.cancelFunction,
          },
          {
            text: modalSettings?.confirmTextButton as string,
            handler: modalSettings?.confirmFunction,
          },
        ]}
      />
    </>
  );
};

export { PayrollSettingsDropdown };
